/* @flow */


import { isCCPAOptedOut } from './ccpautils';
import { isDNT, isGPC } from './browserutil';
import { getFromAllStore } from './storageutils';

/**
 * @module utils/privacyutils
 * @description Privacy utility functions - consolidated privacy signal checks
 * @requires utils/ccpautils
 * @requires utils/browserutil
 * @requires utils/storageutils
 */

// User ID opt-out cookie names
// Refer https://docs.prebid.org/dev-docs/modules/userId.html#user-id-gdpr-permissions-and-opt-out
const OPT_OUT_IDS = ['_pbjs_id_optout', '_pubcid_optout'];

// Cache for isCookieOptedOut() function (only expensive operation)
let OPT_OUT_CACHE_TTL: number = 30; // seconds
let cachedCookieOptOutResult: boolean = false;
let lastCookieOptOutCheckTime: number = 0;

/**
 * Checks if user has opted out via User ID opt-out cookies
 * Results are cached for 30 seconds to avoid repeated cookie/storage reads
 *
 * @returns {boolean} - true if any User ID opt-out cookie is present, false otherwise
 */
function isCookieOptedOut(): boolean {
  const curTime = Date.now() / 1000;

  // Check if we can use cached result
  if (
    lastCookieOptOutCheckTime > 0 &&
    curTime - lastCookieOptOutCheckTime <= OPT_OUT_CACHE_TTL
  ) {
    return cachedCookieOptOutResult;
  }

  // Check User ID opt-out cookies (expensive operation - reading from storage)
  cachedCookieOptOutResult = OPT_OUT_IDS.some(
    cookieName => getFromAllStore(cookieName).length > 0
  );
  lastCookieOptOutCheckTime = curTime;

  return cachedCookieOptOutResult;
}

/**
 * Checks if user has opted out based on all privacy signals
 * Checks GPC, CCPA opt-out, DNT, and User ID opt-out cookies
 * Only the User ID cookie check is cached (CCPA has its own cache, DNT and GPC are cheap)
 *
 * @returns {boolean} - true if any opt-out signal is detected, false otherwise
 */
export function isOptedOut(): boolean {
  // Check all privacy signals
  // GPC is checked first as it's a direct user preference signal
  // CCPA check has its own caching in ccpautils.js
  // DNT and GPC checks are just reading browser properties (very cheap)
  // Only User ID cookie check needs caching here
  return isGPC() || isCCPAOptedOut() || isDNT() || isCookieOptedOut();
}

// Re-export privacy functions from ccpautils.js
export { getCCPA, isCCPAOptedOut, getGDPRConsent, isFromEU } from './ccpautils';

// Re-export privacy functions from browserutil.js
export { isDNT, isGPC } from './browserutil';

/**
 * Reset the opt-out cache for testing purposes
 * @returns {undefined}
 */
export function resetOptOutCache(): void {
  cachedCookieOptOutResult = false;
  lastCookieOptOutCheckTime = 0;
}

/**
 * Set opt-out cache TTL for testing purposes
 * @param {number} ttl - TTL in seconds
 * @returns {undefined}
 */
export function setOptOutCacheTTL(ttl: number): void {
  OPT_OUT_CACHE_TTL = ttl;
}
